import React, { Fragment, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import moment from 'moment';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { themes } from 'Components/widgets/alternative-proposals/alternative-price/AlternativePrice.themes';
import {
  ALL_INCLUSIVE_SERVICE_LIST_DESKTOP_OVERFLOW_THRESHOLD,
  ALL_INCLUSIVE_SERVICE_LIST_MOBILE_OVERFLOW_THRESHOLD,
} from 'Components/widgets/alternative-proposals/alternative-price/constants';
import { OPEN_CRITERIA_TRANSPORT_EVENT } from 'Components/widgets/criteria/criteria-transport/constants';
import { OPEN_CRITERIA_WHEN_EVENT } from 'Components/widgets/criteria/criteria-when/constants';
import { OPEN_CRITERIA_WHO_EVENT } from 'Components/widgets/criteria/criteria-who/constants';
import { t } from 'Src/i18n';
import { DEFAULT_DEPARTURE_CITY } from 'Store/trip-criteria/departure-city/constants';
import { BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { getAttendeesSummary } from 'Utils/label/labelUtils';
import { Button } from 'Components/frameworks/button/Button';
import { Collapse } from 'react-collapse';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Price } from 'Components/frameworks/price/Price';
import { StripButton } from 'Components/frameworks/strip-button/StripButton';
import { AlternativePriceCriteriaButton } from 'Components/widgets/alternative-proposals/alternative-price-criteria-button/AlternativePriceCriteriaButton';
import { ProductBanner } from 'Components/widgets/product-banner/ProductBanner';
import { ProductService } from 'Components/widgets/product-services/product-service/ProductService';
type Props = {
  bookingEngineEntryPoint?: string;
  continueToBookingEngine: () => Promise<any>;
  departureCities: ReadonlyArray<{
    name: string;
    value: string;
  }>;
  hasOtherProposals: boolean;
  onModify?: () => void;
  proposals: ReadonlyArray<Proposal>;
  resort: Product;
  searchCriteria: ProposalSearchCriteria;
  seeOtherProposalsCallback: () => void;
  theme?: keyof typeof themes;
};
export const AlternativePrice = ({
  bookingEngineEntryPoint,
  continueToBookingEngine,
  departureCities,
  hasOtherProposals,
  onModify,
  proposals,
  resort,
  searchCriteria: { adultsCount, arrivalDate, departureCity, duration, kidsCount },
  seeOtherProposalsCallback,
  theme = 'default',
}: Props) => {
  const { isDesktop } = useResponsive();
  const [isDetailOpened, setIsDetailOpened] = useState(false);
  const track = useTracking();
  const { price, allInclusiveServices } = proposals[0];

  if (!price) {
    return null;
  }

  const { thRoot, thSubmitButton } = themes[theme];
  const noTransport = departureCity === DEFAULT_DEPARTURE_CITY;
  const openCriteriaWhenEvent = document.createEvent('Event');
  openCriteriaWhenEvent.initEvent(OPEN_CRITERIA_WHEN_EVENT, true, true);
  const openCriteriaWhoEvent = document.createEvent('Event');
  openCriteriaWhoEvent.initEvent(OPEN_CRITERIA_WHO_EVENT, true, true);
  const openCriteriaTransportEvent = document.createEvent('Event');
  openCriteriaTransportEvent.initEvent(OPEN_CRITERIA_TRANSPORT_EVENT, true, true);

  const criteriaClickHandlerFactory = (event) => () => {
    if (onModify) {
      onModify();
    }

    if (bookingEngineEntryPoint === BOOKING_ENGINE_ENTRIES.PRICE_REMOTE) {
      setTimeout(() => {
        document.dispatchEvent(event);
      }, 200);
    }
  };

  const firstDay = moment(arrivalDate);
  const city = departureCities.find((d) => departureCity === d.value);
  const { bestPrice, total } = price;
  const overflowThreshold = isDesktop
    ? ALL_INCLUSIVE_SERVICE_LIST_DESKTOP_OVERFLOW_THRESHOLD
    : ALL_INCLUSIVE_SERVICE_LIST_MOBILE_OVERFLOW_THRESHOLD;
  return (
    <Fragment>
      {hasOtherProposals && (
        <Button
          className="lg:mt-30"
          color={isDesktop ? COLORS.WHITE : COLORS.SAFFRON}
          colorBg={isDesktop ? COLORS.SAFFRON : COLORS.LIGHT_GREY}
          colorBorder={isDesktop ? COLORS.SAFFRON : COLORS.LIGHT_GREY}
          onClick={() => {
            track({
              actionGA: 'checkPriceKO',
              categoryGA: 'resort',
              labelGA: 'seeOtherProposals',
            });
            seeOtherProposalsCallback();
          }}
        >
          {t('booking.counterProposalPopIn.seeOtherProposalsButton')}
        </Button>
      )}

      <div className={classnames(thRoot)}>
        <div className="flex flex-col items-center p-10">
          <div className="flex w-full justify-between">
            <ProductBanner product={resort} />
            <div className="w-full text-center lg:flex lg:w-auto lg:items-center lg:text-right">
              <div className="hidden gap-10 lg:flex lg:items-center">
                <Icon className="text-saffron" name={ICONS.ALL_INCLUSIVE} width="1.5rem" />
                <div className="font-brand text-16 text-saffron">
                  {t('booking.priceDetails.packagePrice.allInclusive')}
                </div>
              </div>
              <Price
                bestPrice={bestPrice}
                className="inline-flex"
                hasInitialPrice
                hasInstalment={false}
                initialPrice={total}
                unit={t('booking.priceDetails.packagePrice.totalPrice')}
              />
            </div>
          </div>
          <div className="flex-between mt-10 flex w-full flex-wrap lg:flex-nowrap">
            <AlternativePriceCriteriaButton
              iconName={ICONS.DATE}
              onClick={onModify ? criteriaClickHandlerFactory(openCriteriaWhenEvent) : undefined}
            >
              {`${firstDay.format('DD MMM')} - ${firstDay.add(duration, 'd').format('DD MMM')}`}
            </AlternativePriceCriteriaButton>
            <AlternativePriceCriteriaButton
              iconName={ICONS.PEOPLE_DOUBLE}
              onClick={onModify ? criteriaClickHandlerFactory(openCriteriaWhoEvent) : undefined}
            >
              {getAttendeesSummary({
                adultsCount,
                kidsCount,
              })}
            </AlternativePriceCriteriaButton>
            <AlternativePriceCriteriaButton
              iconName={noTransport ? ICONS.NO_TRANSPORT : ICONS.TRANSPORT}
              onClick={
                onModify ? criteriaClickHandlerFactory(openCriteriaTransportEvent) : undefined
              }
            >
              {city ? city.name : ''}
            </AlternativePriceCriteriaButton>
            <Button
              className={classnames(thSubmitButton, 'w-full min-w-80 lg:w-[250px]')}
              onClick={() => {
                track({
                  actionGA: 'checkPriceKO',
                  categoryGA: 'resort',
                  labelGA: 'rate_check',
                });
                continueToBookingEngine();
              }}
              type="submit"
            >
              {t('common.continue')}
            </Button>
          </div>
          {onModify && (
            <Button
              color={COLORS.SAFFRON}
              colorBg={COLORS.TRANSPARENT}
              colorBorder={COLORS.SAFFRON}
              iconLeft={{
                name: ICONS.PEN,
              }}
              onClick={() => {
                track({
                  actionGA: 'checkPriceKO',
                  categoryGA: 'resort',
                  labelGA: 'rate_modify',
                });
                onModify();
              }}
            >
              {t('common.modify')}
            </Button>
          )}
          <div className="w-full px-25">
            <div className="flex flex-wrap">
              {allInclusiveServices
                ?.slice(0, overflowThreshold)
                .map((service) => (
                  <ProductService
                    service={service}
                    className="w-full lg:w-1/2"
                    key={service.label}
                  />
                ))}
            </div>
            {allInclusiveServices?.length > overflowThreshold && (
              <Fragment>
                <Collapse isOpened={isDetailOpened}>
                  <div className="flex-wra flex">
                    {allInclusiveServices
                      .slice(overflowThreshold, allInclusiveServices.length)
                      .map((service) => (
                        <ProductService
                          service={service}
                          className="w-full lg:w-1/2"
                          key={service.label}
                        />
                      ))}
                  </div>
                </Collapse>
                {!isDetailOpened ? <div className="w-full" /> : null}
              </Fragment>
            )}
          </div>
        </div>
        {allInclusiveServices?.length > overflowThreshold && (
          <div className="absolute left-1/2 flex -translate-x-1/2 transform items-center justify-center">
            <StripButton
              className="text-center text-12 font-bold text-saffron underline"
              height={30}
              onClick={() => setIsDetailOpened(!isDetailOpened)}
              orientation="bottom"
            >
              <div className="flex items-center gap-10 text-14">
                {!isDetailOpened
                  ? t('priceRemote.openAllInclusiveDetails')
                  : t('priceRemote.closeAllInclusiveDetails')}
                <Icon
                  className="transition-transform text-saffron"
                  name={ICONS.ARROW_DOWN}
                  rotation={isDetailOpened ? 180 : 0}
                  width="0.625rem"
                />
              </div>
            </StripButton>
          </div>
        )}
      </div>
    </Fragment>
  );
};
